import { SubStage, Tracking, TrackingStage } from './types';

export interface StageLayout {
  stage: TrackingStage;
  /** Share of the bar width, 0-100. */
  widthPct: number;
  /** Where the stage starts on the bar, 0-100. */
  startPct: number;
  /** Position in the original stage list, so colours stay stable when a stage is skipped. */
  colorIndex: number;
}

/** Position each weighted stage on the bar: width proportional to its weight, end to end. */
export const layoutStages = (stages: TrackingStage[]): StageLayout[] => {
  const totalWeight = stages.reduce((sum, stage) => sum + stage.weight, 0);
  if (!totalWeight) return [];
  let startPct = 0;
  return stages.reduce<StageLayout[]>((layout, stage, colorIndex) => {
    if (stage.weight <= 0) return layout;
    const widthPct = (stage.weight / totalWeight) * 100;
    layout.push({ stage, widthPct, startPct, colorIndex });
    startPct += widthPct;
    return layout;
  }, []);
};

/**
 * The current sub-stage: the stored one when given, else inferred from the current
 * stage's progress (sub-stages split their stage evenly).
 */
export const currentSubStage = (
  stages: TrackingStage[],
  currentStageId: number | null,
  currentSubStageId: number | null = null
): SubStage | null => {
  const stage = stages.find(item => item.id === currentStageId);
  if (!stage || !stage.subStages.length) return null;
  const stored = stage.subStages.find(item => item.id === currentSubStageId);
  if (stored) return stored;
  const count = stage.subStages.length;
  return stage.subStages[Math.min(count - 1, Math.floor((stage.progress / 100) * count))];
};

// The rules below mirror backend/portfolio/tracking.py (total_progress, derive_progress);
// keep them in sync. Same examples in both test suites.

/** Overall progress (0-100): each stage's progress weighted by its weight. */
export const totalProgress = (stages: TrackingStage[]): number => {
  const weight = stages.reduce((sum, stage) => sum + stage.weight, 0);
  if (!weight) return 0;
  const weighted = stages.reduce((sum, stage) => sum + stage.weight * stage.progress, 0);
  return Math.round((weighted / weight) * 100) / 100;
};

const toProgress = (value: unknown): number => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.min(100, Math.max(0, number)) : 0;
};

/** Where a project is: the current stage, its sub-stage (none without sub-stages) and the % of it. */
export interface TrackingPosition {
  currentStage: number | null;
  currentSubStage: number | null;
  currentProgress: unknown;
}

/**
 * Each stage's progress (0-100), in order, from the current position: earlier stages
 * done, later ones untouched, and the current one `(index + %/100) / count` through its
 * sub-stages (or the % itself when it has none).
 */
export const deriveProgress = (stages: TrackingStage[], position: TrackingPosition): number[] => {
  const current = stages.findIndex(stage => stage.id === position.currentStage);
  const avance = toProgress(position.currentProgress);
  return stages.map((stage, index) => {
    if (current < 0 || index > current) return 0;
    if (index < current) return 100;
    const count = stage.subStages.length;
    if (!count) return avance;
    const subIndex = Math.max(0, stage.subStages.findIndex(item => item.id === position.currentSubStage));
    return Math.round(((subIndex + avance / 100) / count) * 100 * 100) / 100;
  });
};

/** A Tracking built from a position and raw stage values (e.g. form state), for a live preview. */
export const buildTracking = (
  stages: Array<Omit<TrackingStage, 'progress'> & { progress?: unknown }>,
  position: TrackingPosition,
  extra: Partial<Pick<Tracking, 'lastUpdate' | 'description'>> = {}
): Tracking => {
  const normalized = stages.map(stage => ({
    ...stage,
    progress: 0,
    subStages: stage.subStages || [],
    startDate: stage.startDate || null,
    expectedEndDate: stage.expectedEndDate || null
  }));
  const progress = deriveProgress(normalized, position);
  const derived = normalized.map((stage, index) => ({ ...stage, progress: progress[index] }));
  return {
    hasTracking: true,
    lastUpdate: extra.lastUpdate || null,
    description: extra.description || '',
    totalProgress: totalProgress(derived),
    currentStage: position.currentStage,
    currentSubStage: position.currentSubStage,
    currentProgress: toProgress(position.currentProgress),
    stages: derived
  };
};

/** `YYYY-MM-DD` -> `DD/MM/YY`. */
export const formatShortDate = (isoDate: string | null): string => {
  if (!isoDate) return '';
  const [year, month, day] = isoDate.slice(0, 10).split('-');
  return `${day}/${month}/${year.slice(-2)}`;
};
